import React, { type MouseEventHandler } from 'react';

import { Button } from '@/components/design-system/Button';
import { Input } from '@/components/design-system/input';
import { IconWrapper } from '@/components/ui/IconWrapper';
import { cn } from '@/utils/cn';

type Status = 'default' | 'success' | 'error' | 'warning';

interface InputWithIconProps extends React.ComponentProps<typeof Input> {
  leftIcon?: IconComponent;
  rightIcon?: IconComponent;
  onRightIconClick?: MouseEventHandler<HTMLButtonElement> | (() => void);
  error?: boolean;
  status?: Status;
  width?: string;
}

type IconComponent = React.ComponentType<{ className?: string }>;

export const InputWithIcon: React.FC<InputWithIconProps> = ({
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  onRightIconClick,
  error,
  status = 'default',
  width = 'w-81',
  className,
  ...props
}) => {
  const iconBase = 'absolute top-1/2 text-lg transition-colors duration-300 -translate-y-1/2';

  const leftIconClass = cn(
    iconBase,
    'left-3.5',
    status === 'error'
      ? 'text-error-500'
      : status === 'success'
        ? 'text-success-500'
        : status === 'warning'
          ? 'text-warning-500'
          : 'text-typography-900/45'
  );

  const rightIconClass = cn(
    iconBase,
    'right-3.5 mt-px',
    status === 'error'
      ? 'text-error-500'
      : status === 'success'
        ? 'text-success-500'
        : status === 'warning'
          ? 'text-warning-500'
          : 'text-typography-900/45'
  );

  const inputClass = cn(
    'text-typography-900 placeholder:text-secondary-900/25 border-border-100 h-10 w-full text-base transition-colors duration-300',
    LeftIcon && 'pl-10',
    RightIcon && 'pr-10',
    status === 'error' && 'border-error-500 bg-error-50 focus-visible:border-error-500 hover:border-error-500',
    status === 'success' && 'focus-visible:border-success-500 hover:border-success-500',
    status === 'warning' && 'border-warning-500 focus-visible:border-warning-500 hover:border-warning-500'
  );

  const { 'data-testid': dataTestId, ...inputProps } = props as InputWithIconProps & { 'data-testid'?: string };

  return (
    <div className={cn('relative transition-all duration-300', width, className)}>
      {LeftIcon ? <IconWrapper icon={LeftIcon} className={leftIconClass} /> : null}

      <Input {...inputProps} data-testid={dataTestId} status={status} className={inputClass} />

      {RightIcon ? (
        onRightIconClick ? (
          <Button
            type="button"
            variant="ghost"
            size="condensed"
            onClick={onRightIconClick}
            className={cn(rightIconClass, 'h-auto w-auto p-0 hover:bg-transparent')}
          >
            <IconWrapper icon={RightIcon} />
          </Button>
        ) : (
          <IconWrapper icon={RightIcon} className={rightIconClass} />
        )
      ) : null}
    </div>
  );
};

export default InputWithIcon;
